import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { ClockCounterClockwise, User, Spinner } from "@phosphor-icons/react";

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Entity-anchored history list. Renders the full timeline of activity_log
 * entries that target a single resource (resource_type + resource_id).
 *
 * Usage:
 *   <EntityHistory companyId={cid} resourceType="remittance" resourceId={advice.id} />
 */
export default function EntityHistory({ companyId, resourceType, resourceId, compact = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!companyId || !resourceType || !resourceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(
        `/activity/timeline?company_id=${companyId}&resource_type=${encodeURIComponent(resourceType)}&resource_id=${encodeURIComponent(resourceId)}`
      );
      setItems(data || []);
    } catch (e) {
      console.warn("Failed to load entity history:", e);
    }
    finally { setLoading(false); }
  }, [companyId, resourceType, resourceId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-[#525252] py-3" data-testid="entity-history-loading">
        <Spinner size={14} className="animate-spin" /> Loading history…
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="text-xs text-[#525252] py-3" data-testid="entity-history-empty">
        No history yet.
      </div>
    );
  }

  return (
    <div className="relative" data-testid="entity-history">
      <div className="absolute left-[7px] top-2 bottom-2 w-px bg-[#E5E5E5]" />
      <ul className={`space-y-${compact ? "2" : "3"}`}>
        {items.map((a) => (
          <li key={a.id} className="relative pl-6">
            <span className="absolute left-0 top-1.5 w-[15px] h-[15px] bg-white border border-[#A3A3A3] flex items-center justify-center">
              <ClockCounterClockwise size={9} weight="duotone" className="text-[#525252]" />
            </span>
            <div className="text-xs">
              <div>
                <span className="font-medium">{a.user_name}</span>{" "}
                <span className="text-[#525252]">{a.action}</span>
                {a.details && <span className="text-[#0A0A0A]"> — {a.details}</span>}
              </div>
              {(a.before_status || a.after_status) && (
                <div className="text-[10px] text-[#525252] mt-0.5">
                  {a.before_status || "—"} → <b className="text-[#0A0A0A]">{a.after_status || "—"}</b>
                </div>
              )}
              <div className="text-[10px] text-[#A3A3A3] mt-0.5 flex items-center gap-1">
                <User size={9} weight="duotone" /> {a.user_role || "—"} ·{" "}
                <span title={new Date(a.created_at).toLocaleString()}>{relTime(a.created_at)}</span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
